import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/lib/format";
import { ExternalLink, Clock, Lock, Sparkles, Check, Info } from "lucide-react";
import { ServiceInfoModal } from "@/components/ServiceInfoModal";

const NEON = "#E3FF00";

function ServiceApplyModal({ user, org, onClose }) {
  const [f, setF] = useState({
    company_name: org?.name || "",
    contact_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    website: "",
    service_name: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const inp = "w-full bg-white border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/20";
  const submit = async () => {
    if (!f.service_name.trim() || !f.description.trim()) return toast.error("Indica il nome del servizio e una descrizione");
    setSaving(true);
    try {
      await api.post("/service-submissions", f);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-2xl w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="service-apply-modal">
        {sent ? (
          <div className="text-center py-6 space-y-4" data-testid="service-apply-success">
            <div className="h-16 w-16 rounded-2xl grid place-items-center mx-auto" style={{ backgroundColor: NEON }}>
              <Check className="h-8 w-8 text-neutral-900" />
            </div>
            <h3 className="font-display text-xl font-bold text-neutral-900">Grazie per la tua candidatura!</h3>
            <p className="text-sm text-neutral-600 max-w-sm mx-auto">Valuteremo il tuo servizio e ti faremo sapere al più presto. Ti abbiamo inviato una conferma via email.</p>
            <button onClick={onClose} className="bg-[#E3FF00] text-neutral-900 font-semibold px-6 py-2.5 rounded-lg hover:brightness-95 transition-all" data-testid="apply-close-button">Chiudi</button>
          </div>
        ) : (
          <>
            <div>
              <h3 className="font-display text-xl font-bold text-neutral-900">Candida il tuo servizio</h3>
              <p className="text-sm text-neutral-600 mt-1">Presenta il tuo servizio: il nostro team lo valuterà per integrarlo sulla piattaforma.</p>
            </div>
            <input className={inp} placeholder="Nome azienda" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="apply-company-input" />
            <input className={inp} placeholder="Referente" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="apply-contact-input" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input className={inp} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="apply-email-input" />
              <input className={inp} placeholder="Telefono" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="apply-phone-input" />
            </div>
            <input className={inp} placeholder="Sito web (se esiste)" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="apply-website-input" />
            <input className={inp} placeholder="Nome del servizio proposto" value={f.service_name} onChange={(e) => setF({ ...f, service_name: e.target.value })} data-testid="apply-service-input" />
            <textarea className={inp + " min-h-28"} placeholder="Descrivi il servizio che vuoi proporre..." value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="apply-description-input" />
            <button onClick={submit} disabled={saving} className="w-full bg-[#E3FF00] text-neutral-900 font-semibold py-2.5 rounded-lg hover:brightness-95 disabled:opacity-60 transition-all" data-testid="apply-submit-button">{saving ? "Invio..." : "Invia candidatura"}</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Hub() {
  const { user, org } = useAuth();
  const [services, setServices] = useState([]);
  const [showApply, setShowApply] = useState(false);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState({});
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/services/catalog").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/public/service-profiles").then(({ data }) => setProfiles(Object.fromEntries(data.map((p) => [p.key, p])))).catch(() => {});
  }, []);

  const open = async (s) => {
    if (s.status !== "active" || !s.enabled) return;
    if (s.path) { navigate(s.path); return; }
    if (s.sso) {
      try {
        const { data } = await api.post("/integrations/hw-motor/sso");
        if (data.portal_url) window.open(data.portal_url, "_blank", "noopener,noreferrer");
        else toast.error("Accesso a Home Smart non disponibile");
      } catch (e) { toast.error(e?.response?.data?.detail || "Accesso a Home Smart non riuscito"); }
      return;
    }
    if (s.url) window.open(s.url, "_blank", "noopener,noreferrer");
    else toast.message("Servizio in configurazione — URL non ancora impostato");
  };

  return (
    <div className="-mx-4 md:-mx-6 -my-5 md:-my-7 px-4 md:px-8 py-8 md:py-12 min-h-[calc(100vh-7rem)]" style={{ backgroundColor: NEON }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-neutral-900/60">{org?.name}</p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-neutral-900 mt-1">Ciao {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-neutral-900/70 mt-2 text-lg">I tuoi servizi Mypasspartu</p>
          </div>
          <div className="bg-neutral-900 text-white rounded-2xl px-5 py-3">
            <div className="text-[11px] uppercase tracking-wider opacity-70">Credito borsellino</div>
            <div className="font-mono-data font-bold text-2xl" style={{ color: NEON }}>{eur(org?.wallet_balance_cents)}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {services.map((s, i) => {
            const locked = s.status === "coming_soon" || !s.enabled;
            return (
              <div key={s.key} className="relative">
              <button onClick={() => setInfo(s)} data-testid={`service-info-btn-${s.key}`}
                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-[#E3FF00] text-neutral-900 text-xs font-semibold px-2.5 py-1.5 rounded-lg hover:brightness-95 transition-[filter]">
                <Info className="h-3.5 w-3.5" /> Info
              </button>
              <button onClick={() => open(s)} disabled={locked}
                data-testid={`service-card-${s.key}`}
                className={`group w-full h-full flex flex-col items-center text-center bg-white rounded-2xl border-2 p-8 transition-all shadow-[0_2px_0_rgba(0,0,0,0.06)] animate-in-up ${locked ? "border-neutral-900/5 opacity-70 cursor-not-allowed" : "border-neutral-900/10 hover:border-neutral-900 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(0,0,0,0.12)]"}`}
                style={{ animationDelay: `${i * 60}ms` }}>
                <div className="h-28 w-full grid place-items-center mb-5 overflow-hidden rounded-xl" style={s.logo_bg ? { backgroundColor: s.logo_bg } : undefined}>
                  {s.logo_url ? <img src={s.logo_url} alt={s.name} className={`object-contain ${s.logo_bg ? "max-h-16 max-w-[80%]" : "max-h-28 max-w-[85%]"}`} />
                    : <span className="font-display font-extrabold text-5xl text-neutral-300">{s.name.slice(0, 1)}</span>}
                </div>
                <h3 className="font-display text-xl font-bold text-neutral-900 flex items-center justify-center gap-1.5">
                  {s.name}
                  {!locked && <ExternalLink className="h-5 w-5 text-neutral-400 group-hover:text-neutral-900 group-hover:translate-x-1 transition-all" />}
                </h3>
                <p className="text-sm text-neutral-600 mt-1.5 leading-relaxed">{s.description}</p>
                {s.status === "coming_soon" ? (
                  <span className="inline-flex items-center gap-1 mt-4 text-xs font-semibold text-neutral-500 bg-neutral-100 px-2 py-1 rounded-md"><Clock className="h-3.5 w-3.5" /> In arrivo</span>
                ) : !s.enabled ? (
                  <span className="inline-flex items-center gap-1 mt-4 text-xs font-semibold text-neutral-700 bg-neutral-100 px-2 py-1 rounded-md" data-testid={`service-locked-${s.key}`}><Lock className="h-3.5 w-3.5" /> Non attivo — Richiedi attivazione</span>
                ) : (
                  <span className="inline-flex items-center gap-1 mt-4 text-xs font-semibold text-emerald-700 bg-emerald-500/10 px-2 py-1 rounded-md">Attivo</span>
                )}
              </button>
              </div>
            );
          })}
        </div>

        <div className="mt-8 bg-neutral-900 text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: NEON }}>
              <Sparkles className="h-6 w-6 text-neutral-900" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold">Hai un servizio da proporre?</h3>
              <p className="text-white/70 text-sm mt-1 max-w-xl">Candida il tuo servizio: lo valuteremo per integrarlo sulla piattaforma Mypasspartu.it e offrirlo alle aziende.</p>
            </div>
          </div>
          <button onClick={() => setShowApply(true)} data-testid="service-apply-cta"
            className="bg-[#E3FF00] text-neutral-900 font-semibold px-6 py-3 rounded-lg hover:brightness-95 active:scale-[0.98] transition-all shrink-0 whitespace-nowrap">
            Candida il tuo servizio
          </button>
        </div>
      </div>
      {showApply && <ServiceApplyModal user={user} org={org} onClose={() => setShowApply(false)} />}
      {info && <ServiceInfoModal profile={profiles[info.key] || { ...info, short_description: info.description, description: "" }} onClose={() => setInfo(null)}
        action={info.status === "active" && info.enabled ? (
          <button onClick={() => { open(info); setInfo(null); }} className="w-full bg-[#E3FF00] text-neutral-900 font-semibold py-3 rounded-lg hover:brightness-95 inline-flex items-center justify-center gap-2" data-testid="service-info-open-button">Apri il servizio <ExternalLink className="h-4 w-4" /></button>
        ) : null} />}
    </div>
  );
}
